import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Copy,
  FileText,
  LogOut,
  Pencil,
  Plus,
  Printer,
  Search,
  Trash2,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import Logo from "../components/Logo";
import QuoteDocument from "../components/quotes/QuoteDocument";
import {
  COMMISSION_LEVEL_LABELS,
  PAYMENT_MODES,
  apiError,
  isBattery,
  pageParam,
  powerLabel,
  printQuote,
  dateTimeIt,
  eur,
  findByShortId,
  inputClass,
  kw,
  labelClass,
  pct,
  productTypeLabel,
  quoteNumber,
  shortId,
} from "../components/quotes/format";
import {
  Avatar,
  Card,
  MetaLabel,
  btnDark,
  btnPrimary,
  btnSoft,
} from "../components/quotes/ui";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_STORAGE_KEY = "energeide_agent_token";
// Ogni schermata ha il suo indirizzo:
//   /preventivatore?p=offers                      elenco
//   /preventivatore?p=offers.archive              offerte archiviate
//   /preventivatore?p=offers.new                  nuova offerta
//   /preventivatore?p=offers.detail&qid=b4ccaa86  offerta
//   /preventivatore?p=offers.edit&qid=b4ccaa86    modifica
const LIST_PAGE = { p: "offers" };
const ARCHIVE_PAGE = { p: "offers.archive" };
const offerPage = (q, edit) => ({ p: edit ? "offers.edit" : "offers.detail", qid: shortId(q.id) });
const LOGIN_PHOTO = "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=2000&q=80";

const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeToken = (value) => {
  try {
    if (value) localStorage.setItem(TOKEN_STORAGE_KEY, value);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* storage non disponibile */
  }
};

// -------------------------------------------------------------------
// LOGIN (foto a tutta pagina + riquadro di accesso)
// -------------------------------------------------------------------
const AgentLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/agent/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, pin }),
      });
      if (!res.ok) {
        setError(await apiError(res, "Accesso non riuscito."));
        return;
      }
      const json = await res.json();
      onLogin(json.token);
    } catch {
      setError("Errore di rete. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="h-16 px-4 sm:px-6 flex items-center justify-between">
        <Logo className="h-10 w-auto" />
        <span className="text-[10px] font-montserrat font-bold tracking-[0.2em] text-[#0FB36B]">
          AREA COMMERCIALI
        </span>
      </header>

      <div className="relative flex-1 mx-2 sm:mx-3 mb-2 sm:mb-3 overflow-hidden">
        <img
          src={LOGIN_PHOTO}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/85 via-[#0A1F44]/60 to-[#0A1F44]/40" />

        <div className="relative h-full min-h-[calc(100vh-5rem)] max-w-6xl mx-auto px-4 sm:px-8 py-10 flex flex-col lg:flex-row items-center justify-center lg:justify-between gap-10">
          <div className="text-white text-center lg:text-left max-w-lg">
            <p className="text-xs font-montserrat font-bold tracking-[0.25em] text-[#F4C542] mb-3">
              PREVENTIVATORE
            </p>
            <h1 className="text-3xl sm:text-4xl font-montserrat font-bold leading-tight">
              Preventivi fotovoltaici in pochi minuti
            </h1>
            <p className="mt-4 text-white/75 text-sm sm:text-base">
              Listino, manodopera per regione e spedizioni sempre aggiornati. Tu scegli
              l'impianto, al calcolo pensiamo noi.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="w-full max-w-[340px] bg-white p-7 shadow-2xl"
          >
            <h2 className="text-lg font-montserrat font-bold text-[#0A1F44] mb-5">Accedi</h2>
            <label className={labelClass} htmlFor="agent-email">
              Email
            </label>
            <input
              id="agent-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputClass} mb-4 rounded-none`}
              required
              autoFocus
            />
            <label className={labelClass} htmlFor="agent-pin">
              PIN
            </label>
            <input
              id="agent-pin"
              type="password"
              autoComplete="current-password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className={`${inputClass} rounded-none`}
              required
            />
            <p className="mt-2 text-[11px] text-gray-400">
              PIN dimenticato? Chiedi all'amministratore di reimpostarlo.
            </p>
            {error && (
              <p className="mt-3 flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || !email || !pin}
              className={`${btnPrimary} w-full mt-5 h-11 rounded-none justify-between px-5`}
            >
              {loading ? "Verifica…" : "Accedi"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// ELENCO OFFERTE
// -------------------------------------------------------------------
// Le offerte tolte dall'elenco restano nell'archivio (stato "archiviato" sul server)
const isArchived = (q) => q.status === "archiviato";

const rowActionClass =
  "w-9 h-9 rounded-md flex items-center justify-center text-[#0A1F44]/60 hover:bg-gray-100 hover:text-[#0A1F44] disabled:opacity-40";

const OFFERS_PER_PAGE = 20;

const OfferRow = ({ q, onOpen, onCopy, onArchive, busy, mine, canAct = mine, showOwner }) => {
  const [open, setOpen] = useState(false);
  const subtitle = q.customer?.address || q.region || "Nessun riferimento";
  const archived = isArchived(q);
  // Le offerte degli altri: azioni solo per gli amministratori
  const actions = !canAct ? null : (
    <>
      <button
        type="button"
        title="Copia offerta"
        aria-label="Copia offerta"
        disabled={busy}
        onClick={() => onCopy(q)}
        className={rowActionClass}
      >
        <Copy className="w-4 h-4" />
      </button>
      <button
        type="button"
        title={archived ? "Ripristina dall'archivio" : "Sposta in archivio"}
        aria-label={archived ? "Ripristina dall'archivio" : "Sposta in archivio"}
        disabled={busy}
        onClick={() => onArchive(q, !archived)}
        className={rowActionClass}
      >
        {archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
      </button>
    </>
  );

  return (
    <li className="bg-white rounded-md shadow-[0_1px_3px_rgba(10,31,68,0.08)] hover:shadow-[0_4px_14px_rgba(10,31,68,0.10)] transition-shadow">
      <div className="grid grid-cols-[1fr_auto] md:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto_40px] items-center gap-x-4 gap-y-3 px-4 sm:px-6 py-4">
        <button
          type="button"
          onClick={() => onOpen(q)}
          className="flex items-center gap-3 min-w-0 text-left"
        >
          <Avatar name={q.agent?.name} size="w-9 h-9 text-xs" />
          <span className="min-w-0">
            <span className="block font-montserrat font-bold text-[#0A1F44] hover:text-[#0FB36B]">
              {quoteNumber(q)}
            </span>
            <span className="block text-sm text-[#0A1F44]/70 truncate">{subtitle}</span>
            {showOwner && (
              <span className="block text-xs text-[#0FB36B] truncate">
                {mine ? "Tua offerta" : q.agent?.name}
              </span>
            )}
          </span>
        </button>

        <div className="hidden md:block min-w-0">
          <MetaLabel>Cliente</MetaLabel>
          <p className="text-sm text-[#0A1F44] truncate">{q.customer?.name}</p>
        </div>
        <div className="hidden md:block">
          <MetaLabel>Prezzo di vendita</MetaLabel>
          <p className="text-sm text-[#0A1F44]">{eur(q.price_gross)}</p>
        </div>
        <div className="hidden md:flex items-center gap-1">{actions}</div>
        <button
          type="button"
          aria-label={open ? "Chiudi dettagli" : "Mostra dettagli"}
          onClick={() => setOpen((v) => !v)}
          className="w-9 h-9 rounded-md flex items-center justify-center text-[#0A1F44]/60 hover:bg-gray-100 justify-self-end"
        >
          <ChevronDown className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {/* Mobile: dati compatti sotto il titolo */}
        <div className="md:hidden col-span-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="text-[#0A1F44]">{q.customer?.name}</span>
          <span className="font-semibold text-[#0A1F44]">{eur(q.price_gross)}</span>
          <span className="flex items-center gap-1">{actions}</span>
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-4 sm:px-6 py-4 grid sm:grid-cols-4 gap-4 text-sm">
          <div>
            <MetaLabel>Creata il</MetaLabel>
            <p>{dateTimeIt(q.created_at)}</p>
          </div>
          <div>
            <MetaLabel>Potenza</MetaLabel>
            <p>{q.calc?.total_kw ? kw(q.calc.total_kw) : "—"}</p>
          </div>
          <div>
            <MetaLabel>{mine ? "Tua provvigione" : "Provvigione"}</MetaLabel>
            <p className="font-semibold text-[#0FB36B]">
              {eur(q.commission_amount)}
              {q.input?.commission_level && (
                <span className="font-normal text-gray-400">
                  {" "}
                  ({COMMISSION_LEVEL_LABELS[q.input.commission_level]})
                </span>
              )}
            </p>
          </div>
          <div className="sm:text-right">
            <button type="button" onClick={() => onOpen(q)} className={btnSoft}>
              <FileText className="w-4 h-4" /> Apri offerta
            </button>
          </div>
        </div>
      )}
    </li>
  );
};

const OffersList = ({ quotes, me, loading, archive, onOpen, onNew, onCopy, onArchive, onToggleArchive, busy, error }) => {
  const [query, setQuery] = useState("");
  const [owner, setOwner] = useState("");
  const isAdmin = me.agent.role === "admin";
  const owners = useMemo(() => {
    const map = new Map();
    quotes.forEach((x) => x.agent?.id && map.set(x.agent.id, x.agent.name));
    return [...map].sort((a, b) => a[1].localeCompare(b[1], "it"));
  }, [quotes]);
  const archivedCount = useMemo(() => quotes.filter(isArchived).length, [quotes]);
  const inView = archive ? archivedCount : quotes.length - archivedCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return quotes.filter((x) => {
      if (isArchived(x) !== archive) return false;
      if (owner && x.agent?.id !== owner) return false;
      if (!q) return true;
      return [quoteNumber(x), x.code, x.customer?.name, x.customer?.address, x.region, x.agent?.name]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [quotes, query, archive, owner]);

  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [query, archive, owner]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / OFFERS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const goToPage = (n) => {
    setPage(n);
    window.scrollTo({ top: 0 });
  };
  const visible = filtered.slice((currentPage - 1) * OFFERS_PER_PAGE, currentPage * OFFERS_PER_PAGE);

  return (
    <div className="bg-white/0">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-montserrat font-bold text-[#0A1F44]">
            {archive ? "Archivio offerte" : "Offerte"}
          </h1>
          <button type="button" onClick={onNew} className={`${btnPrimary} h-11 px-6`}>
            <Plus className="w-4 h-4" /> Nuova offerta
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex w-full sm:w-80">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca offerta, cliente, codice…"
              className={`${inputClass} h-11 rounded-r-none border-r-0`}
            />
            <span className="w-11 h-11 shrink-0 bg-[#F4C542] text-[#0A1F44] flex items-center justify-center rounded-r-md">
              <Search className="w-5 h-5" />
            </span>
          </div>
          {isAdmin && (
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              aria-label="Filtra per commerciale"
              className={`${inputClass} h-11 w-full sm:w-56`}
            >
              <option value="">Tutti i commerciali</option>
              {owners.map(([id, name]) => (
                <option key={id} value={id}>
                  {id === me.agent.id ? `${name} (tu)` : name}
                </option>
              ))}
            </select>
          )}
          <button type="button" onClick={onToggleArchive} className={`${btnSoft} h-11`}>
            {archive ? (
              <>
                <ArrowLeft className="w-4 h-4" /> Torna alle offerte
              </>
            ) : (
              <>
                <Archive className="w-4 h-4" /> Archivio ({archivedCount})
              </>
            )}
          </button>
        </div>
        {error && (
          <p className="mb-4 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4 shrink-0" /> {error}
          </p>
        )}

        {loading ? (
          <p className="text-sm text-gray-400">Caricamento…</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-md border border-dashed border-gray-300 p-12 text-center">
            <FileText className="w-9 h-9 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-4">
              {inView > 0
                ? "Nessuna offerta trovata."
                : archive
                  ? "L'archivio è vuoto."
                  : "Non hai ancora creato offerte."}
            </p>
            {!archive && inView === 0 && (
              <button type="button" onClick={onNew} className={btnPrimary}>
                <Plus className="w-4 h-4" /> Crea la prima offerta
              </button>
            )}
          </div>
        ) : (
          <ul className="space-y-3">
            {visible.map((q) => (
              <OfferRow
                key={q.id}
                q={q}
                onOpen={onOpen}
                onCopy={onCopy}
                onArchive={onArchive}
                busy={busy}
                mine={q.agent?.id === me.agent.id}
                canAct={q.agent?.id === me.agent.id || isAdmin}
                showOwner={isAdmin}
              />
            ))}
          </ul>
        )}

        {pageCount > 1 && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
            <span>
              {(currentPage - 1) * OFFERS_PER_PAGE + 1}–{Math.min(currentPage * OFFERS_PER_PAGE, filtered.length)} di{" "}
              {filtered.length}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-8 px-3 rounded-md border border-gray-200 bg-white hover:border-[#0A1F44] disabled:opacity-40"
              >
                Precedente
              </button>
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => goToPage(n)}
                  className={`h-8 min-w-[2rem] px-2 rounded-md border ${
                    n === currentPage
                      ? "bg-[#0A1F44] border-[#0A1F44] text-white"
                      : "bg-white border-gray-200 hover:border-[#0A1F44]"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === pageCount}
                className="h-8 px-3 rounded-md border border-gray-200 bg-white hover:border-[#0A1F44] disabled:opacity-40"
              >
                Successiva
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// PANNELLO RIEPILOGO (colonna destra)
// -------------------------------------------------------------------
const SumRow = ({ label, value, strong, muted }) => (
  <div
    className={`flex justify-between gap-4 py-1 text-sm ${
      strong ? "font-semibold text-[#0A1F44]" : muted ? "text-gray-400" : "text-[#0A1F44]/80"
    }`}
  >
    <dt>{label}</dt>
    <dd className="whitespace-nowrap">{value}</dd>
  </div>
);

// Riepilogo: prodotti e totali di vendita (i costi arrivano solo se il server li espone)
const SummaryPanel = ({ customerName, calc, placeholder, children }) => (
  <aside className="bg-white rounded-md shadow-[0_1px_3px_rgba(10,31,68,0.08)] p-5 lg:sticky lg:top-20">
    <h2 className="text-base font-montserrat font-bold text-[#0A1F44] mb-4">Preventivo</h2>
    <p className="text-sm mb-4">
      <span className="font-semibold text-[#0A1F44]">Cliente: </span>
      <span className="text-[#0A1F44]/70">{customerName || "—"}</span>
    </p>
    {!calc ? (
      <p className="text-sm text-gray-400 py-6">{placeholder}</p>
    ) : (
      <dl>
        <SumRow label="Potenza totale" value={kw(calc.total_kw)} />
        {calc.battery_kwh > 0 && (
          <SumRow label="Accumulo" value={`${calc.battery_kwh.toLocaleString("it-IT")} kWh`} />
        )}

        <div className="my-2 py-2 border-y border-dashed border-gray-200">
          {(calc.lines || []).map((l) => (
            <SumRow
              key={`${l.product_id}|${l.listino_id || ""}`}
              label={`${l.name}${calc.listino === "A misura" && l.listino ? ` (${l.listino})` : ""} × ${Number(l.qty).toLocaleString("it-IT")}`}
              value={l.material_cost !== undefined ? eur(l.material_cost) : ""}
              muted
            />
          ))}
          {(calc.custom_items || []).map((c, i) => (
            <SumRow
              key={`c${i}`}
              label={`${c.description} × ${Number(c.qty).toLocaleString("it-IT")}`}
              value={eur(c.total)}
              muted
            />
          ))}
        </div>

        {calc.material_total !== undefined && <SumRow label="Materiale" value={eur(calc.material_total)} />}
        {calc.custom_total > 0 && <SumRow label="Voci libere" value={eur(calc.custom_total)} />}
        {calc.shipping?.length > 1
          ? calc.shipping.filter((sh) => sh.cost > 0).map((sh) => (
              <SumRow key={sh.supplier} label={`Spedizione ${sh.supplier}`} value={eur(sh.cost)} />
            ))
          : calc.shipping_total !== undefined && (
              <SumRow
                label={`Spedizione ${calc.shipping?.[0]?.supplier || calc.listino || ""}`.trim()}
                value={eur(calc.shipping_total)}
              />
            )}
        {calc.labor_total !== undefined && (
          <SumRow label={`Manodopera ${calc.region || ""}`} value={eur(calc.labor_total)} />
        )}
        {calc.cost_total !== undefined && <SumRow label="Costo totale" value={eur(calc.cost_total)} strong />}
        {calc.margin_amount !== undefined && (
          <SumRow label={`Margine ${calc.phase === "tri" ? "trifase" : "monofase"}`} value={eur(calc.margin_amount)} />
        )}
        <SumRow
          label={`Provvigione${calc.commission_level ? ` ${(COMMISSION_LEVEL_LABELS[calc.commission_level] || "").toLowerCase()}` : ""}`}
          value={eur(calc.commission_amount)}
        />

        <div className="mt-2 pt-2 border-t border-gray-200">
          <SumRow label="Importo netto" value={eur(calc.price_net)} strong />
          <SumRow label={`IVA ${pct(calc.vat_pct)}`} value={eur(calc.price_gross - calc.price_net)} />
        </div>
        <div className="flex justify-between items-baseline mt-3 pt-3 border-t border-gray-200">
          <dt className="font-montserrat font-bold text-[#0A1F44]">Importo lordo:</dt>
          <dd className="text-xl font-montserrat font-bold text-[#0A1F44]">{eur(calc.price_gross)}</dd>
        </div>
        {calc.payment?.installment > 0 && (
          <>
            {/* Tasso zero: quanto paga davvero il cliente (anticipo + rate). Con interessi
                il finanziamento e' tra Findomestic e il cliente: resta solo la rata indicativa. */}
            {calc.payment.plan !== "tan" && (
              <>
                <div className="flex justify-between items-baseline mt-2 pt-2 border-t border-gray-200">
                  <dt className="font-montserrat font-bold text-[#0A1F44]">Prezzo Complessivo:</dt>
                  <dd className="text-lg font-montserrat font-bold text-[#0A1F44]">
                    {eur(calc.payment.deposit + calc.payment.financed)}
                  </dd>
                </div>
                {calc.payment.deposit > 0 && (
                  <SumRow label="di cui anticipo" value={eur(calc.payment.deposit)} />
                )}
              </>
            )}
            <SumRow label={`${calc.payment.months} rate da`} value={eur(calc.payment.installment)} />
          </>
        )}
      </dl>
    )}
    {children && <div className="mt-5 pt-5 border-t border-gray-100 space-y-3">{children}</div>}
  </aside>
);

// -------------------------------------------------------------------
// TESTATA OFFERTA (numero, referenza, utente, ultima modifica)
// -------------------------------------------------------------------
const OfferHeader = ({ title, archived, reference, agentName, createdAt, updatedAt, actions, onBack }) => (
  <Card
    title={
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 hover:text-[#0FB36B]"
      >
        <ArrowLeft className="w-4 h-4" /> Offerte
      </button>
    }
    actions={actions}
  >
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <h1 className="text-xl font-montserrat font-bold text-[#0A1F44]">{title}</h1>
      {archived && (
        <span className="px-2.5 py-1 rounded-full bg-gray-200 text-gray-600 text-[10px] font-montserrat font-bold uppercase tracking-wider">
          Archiviata
        </span>
      )}
    </div>
    <div className="grid sm:grid-cols-3 gap-4 sm:divide-x divide-gray-100">
      <div>
        <MetaLabel>Referenza</MetaLabel>
        <p className="text-sm text-[#0A1F44]">{reference || "Nessun riferimento"}</p>
      </div>
      <div className="sm:pl-4">
        <MetaLabel>Utente assegnato</MetaLabel>
        <div className="flex items-center gap-2">
          <Avatar name={agentName} />
          <div className="text-sm leading-tight">
            <p className="text-[#0A1F44]">{agentName}</p>
            <p className="text-xs text-gray-400">{dateTimeIt(createdAt)}</p>
          </div>
        </div>
      </div>
      <div className="sm:pl-4">
        <MetaLabel>Ultima modifica</MetaLabel>
        <div className="flex items-center gap-2">
          <Avatar name={agentName} />
          <div className="text-sm leading-tight">
            <p className="text-[#0A1F44]">{agentName}</p>
            <p className="text-xs text-gray-400">{dateTimeIt(updatedAt)}</p>
          </div>
        </div>
      </div>
    </div>
  </Card>
);

// -------------------------------------------------------------------
// NUOVA OFFERTA
// Segue lo schema del cliente: 1 cliente, 2 indirizzo/regione, 3 listino,
// 4 moduli, 5 inverter mono/tri, 6 batterie, 7 extra.
// Le scelte dei prodotti sono chiavi "prodotto|listino" (listino vuoto se unico).
// -------------------------------------------------------------------
const CUSTOM_LISTINO = "a_misura";
const optionKey = (productId, listinoId = "") => `${productId}|${listinoId || ""}`;
const newKey = () => Math.random().toString(36).slice(2);
const emptyLine = () => ({ key: newKey(), product_id: "", qty: 1 });
const emptyCustom = () => ({ key: newKey(), description: "", qty: 1, cost: "" });
const emptyCustomer = {
  name: "", email: "", phone: "", address: "", city: "", zip: "", fiscal_code: "", iban: "",
};
const toNumber = (v) => Number(String(v ?? "").replace(",", ".")) || 0;

const optionLabel = (p, showCosts) =>
  [
    p.name,
    powerLabel(p),
    p.listinoName || "",
    showCosts && p.material_cost !== undefined ? eur(p.material_cost) : "",
  ]
    .filter(Boolean)
    .join(" · ");

const Step = ({ n, title, hint, children }) => (
  <div className="grid sm:grid-cols-[200px_minmax(0,1fr)] gap-x-6 gap-y-2 px-5 py-4">
    <div className="flex items-start gap-3">
      <span className="w-6 h-6 shrink-0 rounded-full bg-[#0A1F44] text-white text-[11px] font-montserrat font-bold flex items-center justify-center">
        {n}
      </span>
      <div>
        <p className="text-sm font-montserrat font-semibold text-[#0A1F44] leading-6">{title}</p>
        {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
      </div>
    </div>
    <div className="min-w-0">{children}</div>
  </div>
);

const ProductSelect = ({ products, value, onChange, showCosts, emptyLabel = "Nessuno" }) => (
  <select className={inputClass} value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">{products.length ? emptyLabel : "Nessun prodotto in questo listino"}</option>
    {products.map((p) => (
      <option key={p.id} value={p.id}>
        {optionLabel(p, showCosts)}
      </option>
    ))}
  </select>
);

// inputClass ha w-full: per i campi quantita serve una larghezza fissa
const qtyClass = `${inputClass.replace("w-full", "")} w-24 shrink-0`;

const QtyInput = ({ value, onChange, min = 1, label = "Quantità" }) => (
  <input
    type="number"
    min={min}
    step="1"
    aria-label={label}
    className={`${qtyClass}`}
    value={value}
    onChange={(e) => onChange(e.target.value)}
  />
);

// Modalita' di pagamento + calcolatore Findomestic (conti lato server)
const PaymentCard = ({ payment, setPay, calc, tan, defer = 0 }) => {
  const financed = payment.mode !== "bonifico";
  const result = calc?.payment?.mode === payment.mode ? calc.payment : null;
  const withInterest = result?.plan === "tan";
  return (
    <Card title="Modalità di pagamento">
      <div className="flex flex-wrap gap-2">
        {Object.entries(PAYMENT_MODES).filter(([, m]) => !m.legacy).map(([k, m]) => (
          <button
            key={k}
            type="button"
            onClick={() => setPay("mode")(k)}
            className={`h-10 px-4 rounded-md border text-sm font-montserrat font-semibold transition-colors ${
              payment.mode === k
                ? "bg-[#0A1F44] border-[#0A1F44] text-white"
                : "bg-white border-gray-200 text-[#0A1F44] hover:border-[#0A1F44]"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {payment.mode === "finanziamento" && Number(payment.deposit) > 0
          ? PAYMENT_MODES.finanziamento.textDeposit
          : PAYMENT_MODES[payment.mode]?.text}
      </p>

      {financed && (
        <div className="inline-flex mt-4 rounded-md border border-gray-200 p-0.5 bg-gray-50">
          {[
            ["zero", "Tasso zero"],
            ["tan", `Con interessi · TAN ${pct(tan)}`],
          ].map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setPay("plan")(k)}
              className={`h-9 px-4 rounded text-sm font-semibold transition-colors ${
                payment.plan === k ? "bg-white shadow text-[#0A1F44]" : "text-gray-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        {financed && (
          <div>
            <label className={labelClass}>Anticipo €</label>
            <input
              type="number"
              min="0"
              step="50"
              className={inputClass}
              placeholder="0"
              value={payment.deposit}
              onChange={(e) => setPay("deposit")(e.target.value)}
            />
          </div>
        )}
      </div>

      {result && payment.mode === "bonifico" && (
        <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wider text-gray-400">Anticipo</p>
            <p className="font-semibold text-[#0A1F44]">{eur(result.deposit)}</p>
          </div>
          <div className="rounded-md bg-gray-50 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wider text-gray-400">Saldo</p>
            <p className="font-semibold text-[#0A1F44]">{eur(result.balance)}</p>
          </div>
        </div>
      )}

      {result && financed && (
        <div className="mt-4 overflow-x-auto">
          <p className="text-xs text-gray-500 mb-2">
            {withInterest
              ? `Findomestic credito classico · importo finanziato ${eur(result.financed)} (totale − anticipo + spese di istruttoria). Prima rata a ${(defer + 1) * 30} giorni.`
              : `Findomestic tasso zero · da finanziare ${eur(result.base)} (totale − anticipo).`}{" "}
            Scegli la durata:
          </p>
          <table className="w-full text-sm min-w-[420px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                <th className="py-2 px-2 font-semibold">Mesi</th>
                <th className="py-2 px-2 font-semibold text-right">{withInterest ? "TAN / TAEG" : "Costo %"}</th>
                <th className="py-2 px-2 font-semibold text-right">
                  {withInterest ? "Totale dovuto" : "Prezzo Findomestic"}
                </th>
                <th className="py-2 px-2 font-semibold text-right">Rata</th>
              </tr>
            </thead>
            <tbody>
              {result.options.map((o) => {
                const active = o.months === result.months;
                return (
                  <tr
                    key={o.months}
                    onClick={() => setPay("months")(o.months)}
                    className={`cursor-pointer border-b border-gray-50 ${
                      active ? "bg-[#0FB36B]/10 font-semibold text-[#0A1F44]" : "hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    <td className="py-2 px-2">
                      <input type="radio" readOnly checked={active} className="mr-2 accent-[#0FB36B]" />
                      {o.months}
                    </td>
                    <td className="py-2 px-2 text-right">
                      {withInterest ? `${pct(o.tan)} / ${pct(o.taeg)}` : pct(o.rate)}
                    </td>
                    <td className="py-2 px-2 text-right">{eur(withInterest ? o.total_due : o.financed)}</td>
                    <td className="py-2 px-2 text-right">{eur(o.installment)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-3 border border-[#7a0c0c] px-3 py-2 text-center text-[11px] italic text-gray-600">
            Il prospetto di cui sopra non costituisce offerta al pubblico ai sensi dell'articolo 1336 del Codice Civile. Chieda un Preventivo al suo Venditore.
          </p>
        </div>
      )}
    </Card>
  );
};

// Valori iniziali del modulo: vuoti per una nuova offerta, oppure ricostruiti
// da un'offerta salvata (modifica).
const editorInitialState = (quote, me) => {
  const suppliers = me.suppliers || [];
  const rates = me.limits.finance_rates || [];
  const state = {
    customer: { ...emptyCustomer },
    regionId: "",
    // Listino predefinito: Spartano (se manca, il primo disponibile)
    listinoId: (suppliers.find((s) => s.name.toLowerCase() === "spartano") || suppliers[0])?.id || "",
    retrofit: false,
    moduleId: "",
    moduleQty: "",
    phase: "mono",
    inverterId: "",
    inverterQty: 1,
    batteryId: "",
    batteryQty: 1,
    extras: [],
    customItems: [],
    commissionLevel: "media",
    commissionAmount: "",
    payment: { mode: "bonifico", plan: "tan", deposit: "", months: rates[rates.length - 1]?.months || "" },
    notes: "",
  };
  if (!quote) return state;

  const input = quote.input || {};
  const calc = quote.calc || {};
  const productById = Object.fromEntries(me.products.map((p) => [p.id, p]));
  state.customer = { ...emptyCustomer, ...quote.customer };
  state.regionId =
    input.region_id || me.regions.find((r) => r.name === quote.region)?.id || "";
  state.listinoId =
    input.listino_id || suppliers.find((s) => s.name === calc.listino)?.id || state.listinoId;
  // Offerte create prima dei livelli: la vecchia provvigione diventa "libera"
  state.commissionLevel = input.commission_level || "libera";
  state.commissionAmount = String(Math.round(input.commission_amount ?? quote.commission_amount ?? 0));
  state.retrofit = Boolean(input.retrofit ?? calc.retrofit);
  state.customItems = (input.custom_items || []).map((c) => ({ ...emptyCustom(), ...c, cost: String(c.cost) }));
  if (input.payment) {
    state.payment = {
      // "anticipo_finanziamento" e' la vecchia modalita', ora unita al finanziamento
      mode: input.payment.mode === "anticipo_finanziamento" ? "finanziamento" : input.payment.mode || "bonifico",
      plan: input.payment.plan || "zero",
      // Con il bonifico l'anticipo e' sempre il 50%: non e' un dato inserito
      deposit:
        input.payment.mode !== "bonifico" && input.payment.deposit ? String(input.payment.deposit) : "",
      months: input.payment.months || state.payment.months,
    };
  }
  state.notes = quote.notes || "";

  const custom = state.listinoId === CUSTOM_LISTINO;
  const items = input.items || (calc.lines || []).map((l) => ({ product_id: l.product_id, qty: l.qty }));
  items.forEach(({ product_id: pid, listino_id: lid, qty }) => {
    const id = optionKey(pid, custom ? lid : "");
    const type = productById[pid]?.category || (calc.lines || []).find((l) => l.product_id === pid)?.category;
    if (type === "modulo" && !state.moduleId) {
      state.moduleId = id;
      state.moduleQty = String(qty);
    } else if ((type === "inverter_mono" || type === "inverter_tri") && !state.inverterId) {
      state.phase = type === "inverter_tri" ? "tri" : "mono";
      state.inverterId = id;
      state.inverterQty = qty;
    } else if (isBattery(type) && !state.batteryId) {
      state.batteryId = id;
      state.batteryQty = qty;
    } else {
      state.extras.push({ ...emptyLine(), product_id: id, qty });
    }
  });
  return state;
};

const OfferEditor = ({ me, token, initial, onSaved, onCancel, onUnauthorized }) => {
  const { limits, products, regions, show_costs: showCosts } = me;
  const commissionLevels = limits.commission_levels || {};
  const suppliers = useMemo(() => me.suppliers || [], [me.suppliers]);
  const [init] = useState(() => editorInitialState(initial, me));
  const isEdit = Boolean(initial);
  const [customer, setCustomer] = useState(init.customer);
  const [regionId, setRegionId] = useState(init.regionId);
  const [listinoId, setListinoId] = useState(init.listinoId);
  const [retrofit, setRetrofit] = useState(init.retrofit);
  const [moduleId, setModuleId] = useState(init.moduleId);
  const [moduleQty, setModuleQty] = useState(init.moduleQty);
  const [phase, setPhase] = useState(init.phase);
  const [inverterId, setInverterId] = useState(init.inverterId);
  const [inverterQty] = useState(init.inverterQty);
  const [batteryId, setBatteryId] = useState(init.batteryId);
  const [batteryQty, setBatteryQty] = useState(init.batteryQty);
  const [extras, setExtras] = useState(init.extras);
  const [customItems, setCustomItems] = useState(init.customItems);
  const [commissionLevel, setCommissionLevel] = useState(init.commissionLevel);
  const [commissionAmount, setCommissionAmount] = useState(init.commissionAmount);
  const [payment, setPayment] = useState(init.payment);
  const [notes, setNotes] = useState(init.notes);
  // Ultimo listino/fase gia' applicati: gli effetti sotto azzerano i prodotti solo
  // quando cambiano davvero. In modifica partono dai valori salvati, cosi' le
  // scelte dell'offerta non vengono cancellate all'apertura.
  const appliedListino = useRef(isEdit ? init.listinoId : null);
  const appliedInverterKey = useRef(isEdit ? `${init.listinoId}|${init.phase}` : null);
  const [calc, setCalc] = useState(null);
  const [calcError, setCalcError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const reqId = useRef(0);
  const [now] = useState(() => new Date().toISOString());

  // Prodotti del listino scelto + quelli senza listino (validi per tutti).
  // A misura: tutti i prodotti, una voce per ogni listino in cui compaiono.
  const byType = useMemo(() => {
    const out = {};
    const push = (p, lid) =>
      (out[p.category] ||= []).push({
        ...p,
        id: optionKey(p.id, lid),
        listinoName: lid ? suppliers.find((s) => s.id === lid)?.name : "",
      });
    products.forEach((p) => {
      const lids = p.listino_ids || [];
      if (listinoId === CUSTOM_LISTINO) {
        if (!lids.length) push(p, "");
        lids.forEach((lid) => push(p, lid));
      } else if (!lids.length || lids.includes(listinoId)) {
        push(p, "");
      }
    });
    return (t) => out[t] || [];
  }, [products, listinoId, suppliers]);
  const modules = byType("modulo");
  const inverters = byType(phase === "tri" ? "inverter_tri" : "inverter_mono");
  // Con inverter trifase si propongono prima le batterie trifase
  const batteries = phase === "tri" ? [...byType("batteria_tri"), ...byType("batteria")] : byType("batteria");
  // Colonnina e backup si aggiungono come extra
  const extraProducts = [...byType("extra"), ...byType("colonnina"), ...byType("backup")];

  // Cambio listino: si riparte da zero con i prodotti (sono di un altro fornitore).
  // Se c'e' un solo modulo lo selezioniamo noi.
  useEffect(() => {
    if (appliedListino.current === listinoId) return;
    appliedListino.current = listinoId;
    const mods = byType("modulo");
    setModuleId(mods.length === 1 ? mods[0].id : "");
    setBatteryId("");
    setExtras([]);
  }, [byType, listinoId]);

  // Cambio listino o fase: inverter da riscegliere (auto se ce n'e' uno solo)
  useEffect(() => {
    const key = `${listinoId}|${phase}`;
    if (appliedInverterKey.current === key) return;
    appliedInverterKey.current = key;
    const invs = byType(phase === "tri" ? "inverter_tri" : "inverter_mono");
    setInverterId(invs.length === 1 ? invs[0].id : "");
  }, [byType, listinoId, phase]);

  const items = useMemo(() => {
    const out = [];
    const add = (key, qty) => {
      if (!key || !(Number(qty) > 0)) return;
      const [pid, lid] = key.split("|");
      out.push({ product_id: pid, listino_id: lid || "", qty: Number(qty) });
    };
    if (!retrofit) add(moduleId, moduleQty);
    add(inverterId, inverterQty);
    add(batteryId, batteryQty);
    extras.forEach((l) => add(l.product_id, l.qty));
    return out;
  }, [retrofit, moduleId, moduleQty, inverterId, inverterQty, batteryId, batteryQty, extras]);

  const payload = useCallback(
    () => ({
      region_id: regionId,
      listino_id: listinoId,
      items,
      retrofit,
      custom_items: customItems
        .filter((c) => c.description.trim())
        .map((c) => ({ description: c.description.trim(), qty: toNumber(c.qty), cost: toNumber(c.cost) })),
      phase,
      commission_level: commissionLevel,
      commission_amount: toNumber(commissionAmount),
      payment: {
        mode: payment.mode,
        plan: payment.plan,
        deposit: payment.deposit === "" ? "" : toNumber(payment.deposit),
        months: Number(payment.months) || null,
      },
    }),
    [regionId, listinoId, items, retrofit, customItems, phase, commissionLevel, commissionAmount, payment]
  );

  // Ricalcolo live (lato server) con piccolo debounce
  useEffect(() => {
    const body = payload();
    if (!body.region_id || body.items.length === 0) {
      setCalc(null);
      setCalcError("");
      return undefined;
    }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/agent/quotes/calc`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Agent-Token": token },
          body: JSON.stringify(body),
        });
        if (id !== reqId.current) return;
        if (res.status === 401) return onUnauthorized();
        if (!res.ok) {
          setCalc(null);
          setCalcError(await apiError(res));
          return;
        }
        setCalc(await res.json());
        setCalcError("");
      } catch {
        if (id === reqId.current) setCalcError("Errore di rete.");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [payload, token, onUnauthorized]);

  const submit = async (e) => {
    e.preventDefault();
    setSaveError("");
    if (!retrofit && (!moduleId || !(Number(moduleQty) > 0))) {
      setSaveError("Seleziona l'opzione Retrofit oppure scegli un modulo e la quantità.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(
        isEdit ? `${API_URL}/api/agent/quotes/${initial.id}` : `${API_URL}/api/agent/quotes`,
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json", "X-Agent-Token": token },
          body: JSON.stringify({ ...payload(), customer, notes }),
        }
      );
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) {
        setSaveError(await apiError(res, "Salvataggio non riuscito."));
        return;
      }
      onSaved(await res.json());
    } catch {
      setSaveError("Errore di rete. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const selectedModule = modules.find((m) => m.id === moduleId);
  const moduleKw = selectedModule && !retrofit ? selectedModule.power_kw * (Number(moduleQty) || 0) : 0;
  const regionName = regions.find((r) => r.id === regionId)?.name;
  const setField = (k) => (e) => setCustomer((s) => ({ ...s, [k]: e.target.value }));
  const updateExtra = (key, patch) =>
    setExtras((s) => s.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const updateCustom = (key, patch) =>
    setCustomItems((s) => s.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const setPay = (k) => (v) => setPayment((s) => ({ ...s, [k]: v }));

  return (
    <form
      onSubmit={submit}
      className="max-w-7xl mx-auto px-4 sm:px-8 py-6 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-5 items-start"
    >
      <div className="space-y-5 min-w-0">
        <OfferHeader
          title={isEdit ? `Modifica ${quoteNumber(initial)}` : "Nuova offerta"}
          reference={[customer.address, customer.city, regionName].filter(Boolean).join(" · ")}
          agentName={initial?.agent?.name || me.agent.name}
          createdAt={isEdit ? initial.created_at : now}
          updatedAt={isEdit ? initial.updated_at || initial.created_at : now}
          onBack={onCancel}
        />

        <Card title="Cliente e installazione" bodyClassName="divide-y divide-gray-100">
          <Step n={1} title="Cliente" hint="Nome e cognome o ragione sociale">
            <div className="grid sm:grid-cols-3 gap-3">
              <input
                className={`${inputClass} sm:col-span-3`}
                placeholder="Nome e cognome *"
                value={customer.name}
                onChange={setField("name")}
                required
              />
              <input className={inputClass} placeholder="Cellulare" value={customer.phone} onChange={setField("phone")} />
              <input
                type="email"
                className={`${inputClass} sm:col-span-2`}
                placeholder="Email"
                value={customer.email}
                onChange={setField("email")}
              />
              <input
                className={`${inputClass} uppercase`}
                placeholder="Codice fiscale"
                value={customer.fiscal_code}
                onChange={setField("fiscal_code")}
              />
              <input
                className={`${inputClass} sm:col-span-2 uppercase`}
                placeholder="IBAN"
                value={customer.iban}
                onChange={setField("iban")}
              />
            </div>
          </Step>
          <Step n={2} title="Indirizzo" hint="La regione serve per la manodopera">
            <div className="grid sm:grid-cols-6 gap-3">
              <input
                className={`${inputClass} sm:col-span-6`}
                placeholder="Via e numero civico"
                value={customer.address}
                onChange={setField("address")}
              />
              <input
                className={`${inputClass} sm:col-span-3`}
                placeholder="Comune"
                value={customer.city}
                onChange={setField("city")}
              />
              <input
                className={`${inputClass} sm:col-span-1`}
                placeholder="CAP"
                inputMode="numeric"
                value={customer.zip}
                onChange={setField("zip")}
              />
              <select
                className={`${inputClass} sm:col-span-2`}
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                required
              >
                <option value="">Regione *</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          </Step>
        </Card>

        <Card title="Impianto" bodyClassName="divide-y divide-gray-100">
          <Step n={3} title="Listino" hint="A misura: prodotti di listini diversi">
            {suppliers.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nessun listino disponibile: l'amministratore deve inserire listini e prodotti.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {suppliers.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setListinoId(s.id)}
                    className={`h-10 px-5 rounded-md border text-sm font-montserrat font-semibold transition-colors ${
                      listinoId === s.id
                        ? "bg-[#0A1F44] border-[#0A1F44] text-white"
                        : "bg-white border-gray-200 text-[#0A1F44] hover:border-[#0A1F44]"
                    }`}
                  >
                    {s.name}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setListinoId(CUSTOM_LISTINO)}
                  className={`h-10 px-5 rounded-md border border-dashed text-sm font-montserrat font-semibold transition-colors ${
                    listinoId === CUSTOM_LISTINO
                      ? "bg-[#F4C542] border-[#F4C542] text-[#0A1F44]"
                      : "bg-white border-gray-300 text-[#0A1F44] hover:border-[#0A1F44]"
                  }`}
                >
                  A misura
                </button>
              </div>
            )}
          </Step>

          <Step
            n={4}
            title="Moduli"
            hint={retrofit ? "Impianto esistente: nessun pannello" : moduleKw ? `Potenza impianto: ${kw(moduleKw)}` : "Modello e numero"}
          >
            <label className="flex items-center gap-2 mb-3 text-sm text-[#0A1F44] cursor-pointer w-fit">
              <input
                type="checkbox"
                className="h-4 w-4 accent-[#F4C542]"
                checked={retrofit}
                onChange={(e) => setRetrofit(e.target.checked)}
              />
              <span className="font-semibold">Retrofit</span>
            </label>
            {!retrofit && (
            <div className="flex gap-3">
              <div className="flex-1 min-w-0">
                <ProductSelect
                  products={modules}
                  value={moduleId}
                  onChange={setModuleId}
                  showCosts={showCosts}
                  emptyLabel="Seleziona modulo…"
                />
              </div>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="N°"
                aria-label="Numero moduli"
                className={`${qtyClass}`}
                value={moduleQty}
                onChange={(e) => setModuleQty(e.target.value)}
              />
            </div>
            )}
          </Step>

          <Step n={5} title="Inverter">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="inline-flex self-start shrink-0 rounded-md border border-gray-200 p-0.5 bg-gray-50">
                {[
                  ["mono", "Monofase"],
                  ["tri", "Trifase"],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setPhase(k)}
                    className={`h-9 px-4 rounded text-sm font-semibold transition-colors ${
                      phase === k ? "bg-white shadow text-[#0A1F44]" : "text-gray-500"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex-1 min-w-0">
                <ProductSelect
                  products={inverters}
                  value={inverterId}
                  onChange={setInverterId}
                  showCosts={showCosts}
                  emptyLabel="Seleziona inverter…"
                />
              </div>
            </div>
          </Step>

          <Step
            n={6}
            title="Batterie"
            hint={calc?.battery_kwh ? `Accumulo: ${calc.battery_kwh.toLocaleString("it-IT")} kWh` : "Lascia 'Nessuna' se non previste"}
          >
            <div className="flex gap-3">
              <div className="flex-1 min-w-0">
                <ProductSelect
                  products={batteries}
                  value={batteryId}
                  onChange={setBatteryId}
                  showCosts={showCosts}
                  emptyLabel="Nessuna"
                />
              </div>
              {batteryId && <QtyInput value={batteryQty} onChange={setBatteryQty} />}
            </div>
          </Step>

          <Step n={7} title="Extra" hint="Wallbox, backup, ottimizzatori, cestello, ...">
            <div className="space-y-3">
              {extras.map((l) => (
                <div key={l.key} className="flex gap-3">
                  <div className="flex-1 min-w-0">
                    <ProductSelect
                      products={extraProducts}
                      value={l.product_id}
                      onChange={(v) => updateExtra(l.key, { product_id: v })}
                      showCosts={showCosts}
                      emptyLabel="Seleziona extra…"
                    />
                  </div>
                  <QtyInput value={l.qty} onChange={(v) => updateExtra(l.key, { qty: v })} />
                  <button
                    type="button"
                    aria-label="Rimuovi extra"
                    onClick={() => setExtras((s) => s.filter((x) => x.key !== l.key))}
                    className="h-10 w-10 shrink-0 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {customItems.map((l) => (
                <div key={l.key} className="flex flex-wrap sm:flex-nowrap gap-3">
                  <input
                    className={`${inputClass} flex-1 min-w-[12rem]`}
                    placeholder="Descrizione voce libera"
                    value={l.description}
                    onChange={(e) => updateCustom(l.key, { description: e.target.value })}
                  />
                  <QtyInput value={l.qty} onChange={(v) => updateCustom(l.key, { qty: v })} />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Costo €"
                    aria-label="Costo unitario"
                    className={`${qtyClass} w-28`}
                    value={l.cost}
                    onChange={(e) => updateCustom(l.key, { cost: e.target.value })}
                  />
                  <button
                    type="button"
                    aria-label="Rimuovi voce"
                    onClick={() => setCustomItems((s) => s.filter((x) => x.key !== l.key))}
                    className="h-10 w-10 shrink-0 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setExtras((s) => [...s, emptyLine()])}
                  disabled={extraProducts.length === 0}
                  className={btnSoft}
                >
                  <Plus className="w-4 h-4" /> Aggiungi extra
                </button>
                <button
                  type="button"
                  onClick={() => setCustomItems((s) => [...s, emptyCustom()])}
                  className={btnSoft}
                >
                  <Plus className="w-4 h-4" /> Voce libera
                </button>
              </div>
            </div>
          </Step>
        </Card>

        <Card title="Provvigione">
          <div className="flex flex-wrap gap-2">
            {[
              ...Object.entries(commissionLevels).map(([k, v]) => [k, k[0].toUpperCase() + k.slice(1), eur(v)]),
              ["libera", "Libera", "importo a scelta"],
            ].map(([k, label, sub]) => (
              <button
                key={k}
                type="button"
                onClick={() => setCommissionLevel(k)}
                className={`min-w-[7.5rem] px-4 py-2 rounded-md border text-left transition-colors ${
                  commissionLevel === k
                    ? "bg-[#0A1F44] border-[#0A1F44] text-white"
                    : "bg-white border-gray-200 text-[#0A1F44] hover:border-[#0A1F44]"
                }`}
              >
                <span className="block text-sm font-montserrat font-semibold">{label}</span>
                <span className={`block text-xs ${commissionLevel === k ? "text-white/70" : "text-gray-400"}`}>
                  {sub}
                </span>
              </button>
            ))}
          </div>
          {commissionLevel === "libera" && (
            <div className="mt-4 max-w-xs">
              <label className={labelClass}>Provvigione €</label>
              <input
                type="number"
                min="0"
                step="10"
                max={limits.commission_free_max}
                className={inputClass}
                value={commissionAmount}
                onChange={(e) => setCommissionAmount(e.target.value)}
              />
              <p className="text-[11px] text-gray-400 mt-1">Massimo {eur(limits.commission_free_max)}</p>
            </div>
          )}
        </Card>

        <PaymentCard payment={payment} setPay={setPay} calc={calc} tan={limits.finance_tan} defer={limits.finance_defer} />

        <Card title="Commento">
          <label className={labelClass}>Note sul preventivo (visibili al cliente)</label>
          <textarea
            rows={3}
            className={`${inputClass} h-auto py-2`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Card>
      </div>

      <SummaryPanel
        customerName={customer.name}
        calc={calc}
        showCosts={showCosts}
        placeholder={calcError || "Scegli regione e prodotti per vedere il prezzo."}
      >
        {calc && calcError && <p className="text-sm text-red-600">{calcError}</p>}
        {saveError && (
          <p className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4 shrink-0" /> {saveError}
          </p>
        )}
        <button
          type="submit"
          disabled={saving || !calc || !customer.name.trim()}
          className={`${btnPrimary} w-full h-12`}
        >
          {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea offerta"}
        </button>
        <button type="button" onClick={onCancel} className={`${btnSoft} w-full`}>
          Annulla
        </button>
      </SummaryPanel>
    </form>
  );
};

// -------------------------------------------------------------------
// DETTAGLIO OFFERTA SALVATA
// -------------------------------------------------------------------
const OfferDetail = ({ quote, me, onBack, onEdit }) => {
  const lines = quote.calc?.lines || [];
  const archived = isArchived(quote);

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-5 items-start print:hidden">
        <div className="space-y-5 min-w-0">
          <OfferHeader
            title={quoteNumber(quote)}
            archived={archived}
            reference={[quote.customer?.address, quote.region].filter(Boolean).join(" · ")}
            agentName={quote.agent?.name || me.agent.name}
            createdAt={quote.created_at}
            updatedAt={quote.updated_at || quote.created_at}
            onBack={onBack}
            actions={
              <>
                {!archived && (quote.agent?.id === me.agent.id || me.agent.role === "admin") && (
                  <button type="button" onClick={() => onEdit(quote)} className={btnSoft}>
                    <Pencil className="w-4 h-4" /> Modifica
                  </button>
                )}
                <button type="button" onClick={() => printQuote(quote)} className={btnPrimary}>
                  <Printer className="w-4 h-4" /> Stampa
                </button>
              </>
            }
          />

          <div className="grid md:grid-cols-2 gap-5">
            <Card title="Cliente">
              <p className="text-sm font-semibold text-[#0A1F44]">{quote.customer?.name}</p>
              {quote.customer?.phone && <p className="text-sm text-gray-500">{quote.customer.phone}</p>}
              {quote.customer?.email && <p className="text-sm text-gray-500">{quote.customer.email}</p>}
              {quote.customer?.fiscal_code && (
                <p className="text-sm text-gray-500">CF {quote.customer.fiscal_code}</p>
              )}
              {quote.customer?.iban && <p className="text-sm text-gray-500">IBAN {quote.customer.iban}</p>}
            </Card>
            <Card title="Installazione">
              <p className="text-sm text-[#0A1F44]">{quote.customer?.address || "Indirizzo non indicato"}</p>
              {(quote.customer?.zip || quote.customer?.city) && (
                <p className="text-sm text-[#0A1F44]">
                  {[quote.customer.zip, quote.customer.city].filter(Boolean).join(" ")}
                </p>
              )}
              <p className="text-sm text-gray-500">{quote.region}</p>
              {quote.calc?.listino && (
                <p className="text-sm text-gray-500">Listino: {quote.calc.listino}</p>
              )}
            </Card>
          </div>

          <Card title="Prodotti" bodyClassName="p-0">
            <div className="divide-y divide-gray-100">
              <div className="grid grid-cols-[minmax(0,1fr)_90px_60px] gap-3 px-5 py-2 bg-gray-50">
                <MetaLabel>Prodotto</MetaLabel>
                <MetaLabel>Potenza</MetaLabel>
                <MetaLabel>Q.tà</MetaLabel>
              </div>
              {lines.map((l) => (
                <div key={`${l.product_id}|${l.listino_id || ""}`} className="grid grid-cols-[minmax(0,1fr)_90px_60px] gap-3 px-5 py-3 text-sm">
                  <span className="text-[#0A1F44]">
                    {l.name}
                    {l.category && (
                      <span className="block text-xs text-gray-400">{productTypeLabel(l.category)}</span>
                    )}
                  </span>
                  <span className="text-[#0A1F44]/70">
                    {powerLabel(l) || "—"}
                  </span>
                  <span>{l.qty}</span>
                </div>
              ))}
              {(quote.calc?.custom_items || []).map((c, i) => (
                <div key={`c${i}`} className="grid grid-cols-[minmax(0,1fr)_90px_60px] gap-3 px-5 py-3 text-sm">
                  <span className="text-[#0A1F44]">
                    {c.description}
                    <span className="block text-xs text-gray-400">Voce libera</span>
                  </span>
                  <span className="text-[#0A1F44]/70">—</span>
                  <span>{c.qty}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Commento">
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{quote.notes || "Nessun commento."}</p>
          </Card>
        </div>

        <SummaryPanel customerName={quote.customer?.name} calc={quote.calc} showCosts={me.show_costs}>
          {archived && (
            <p className="text-[11px] text-gray-400">
              Offerta archiviata: per modificarla ripristinala dall'archivio.
            </p>
          )}
          <div className="text-[11px] text-gray-400">
            Codice di riferimento: <span className="font-mono text-gray-500">{quote.code}</span>
          </div>
          <button type="button" onClick={() => printQuote(quote)} className={`${btnDark} w-full h-11`}>
            <Printer className="w-4 h-4" /> Stampa / Salva PDF
          </button>
        </SummaryPanel>
      </div>

      {/* Versione stampabile: visibile solo in stampa */}
      <div className="hidden print:block">
        <QuoteDocument quote={quote} />
      </div>
    </>
  );
};

// -------------------------------------------------------------------
// PAGINA
// -------------------------------------------------------------------
const Preventivatore = () => {
  const [token, setTokenState] = useState(readToken);
  const [me, setMe] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [quotesLoaded, setQuotesLoaded] = useState(false);
  const [params, navigate] = useSearchParams();

  // La schermata si ricava dall'indirizzo: list | new | edit | detail
  const subPage = pageParam(params, "offers")[1];
  const view = ["new", "edit", "detail", "archive"].includes(subPage) ? subPage : "list";
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const quoteId = view === "edit" || view === "detail" ? params.get("qid") : null;
  const openQuote = findByShortId(quotes, quoteId);
  const pageKey = params.toString();

  const setToken = useCallback((value) => {
    writeToken(value);
    setTokenState(value);
    if (!value) setMe(null);
  }, []);

  const onUnauthorized = useCallback(() => setToken(null), [setToken]);

  useEffect(() => {
    if (!token) return;
    setLoadError("");
    fetch(`${API_URL}/api/agent/me`, { headers: { "X-Agent-Token": token } })
      .then(async (res) => {
        if (res.status === 401) return onUnauthorized();
        if (!res.ok) throw new Error(await apiError(res));
        setMe(await res.json());
      })
      .catch((e) => setLoadError(e.message || "Errore di caricamento."));
  }, [token, onUnauthorized]);

  const loadQuotes = useCallback(() => {
    setLoadingQuotes(true);
    fetch(`${API_URL}/api/agent/quotes`, { headers: { "X-Agent-Token": token } })
      .then((r) => (r.status === 401 ? onUnauthorized() : r.ok ? r.json() : { data: [] }))
      .then((json) => setQuotes(Array.isArray(json?.data) ? json.data : []))
      .finally(() => {
        setLoadingQuotes(false);
        setQuotesLoaded(true);
      });
  }, [token, onUnauthorized]);

  useEffect(() => {
    if (me) loadQuotes();
  }, [me, loadQuotes]);

  // Indirizzo di un'offerta che non esiste (o di un altro commerciale): si torna all'elenco
  useEffect(() => {
    if (quotesLoaded && quoteId && !openQuote) navigate(LIST_PAGE, { replace: true });
  }, [quotesLoaded, quoteId, openQuote, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pageKey]);

  // Copia: nuova offerta (nuovo id, numero e codice) con gli stessi dati, ricalcolata dal server
  const copyQuote = async (q) => {
    if (!q.input) {
      setActionError("Questa offerta è troppo vecchia per essere copiata: creane una nuova.");
      return;
    }
    setBusy(true);
    setActionError("");
    try {
      const res = await fetch(`${API_URL}/api/agent/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Agent-Token": token },
        body: JSON.stringify({ ...q.input, customer: q.customer, notes: q.notes }),
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) {
        setActionError(await apiError(res, "Copia non riuscita."));
        return;
      }
      const copy = await res.json();
      setQuotes((s) => [copy, ...s]);
      navigate(LIST_PAGE);
    } catch {
      setActionError("Errore di rete. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  // Archivia / ripristina: l'offerta non viene cancellata
  const archiveQuote = async (q, archived) => {
    setBusy(true);
    setActionError("");
    const status = archived ? "archiviato" : "valutazione";
    try {
      const res = await fetch(`${API_URL}/api/agent/quotes/${q.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-Agent-Token": token },
        body: JSON.stringify({ status }),
      });
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) {
        setActionError(await apiError(res, "Operazione non riuscita."));
        return;
      }
      setQuotes((s) => s.map((x) => (x.id === q.id ? { ...x, status } : x)));
    } catch {
      setActionError("Errore di rete. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    fetch(`${API_URL}/api/agent/logout`, {
      method: "POST",
      headers: { "X-Agent-Token": token },
    }).catch(() => {});
    setToken(null);
  };

  const seo = <Seo title="Preventivatore" path="/preventivatore" noIndex />;

  if (!token) {
    return (
      <>
        {seo}
        <AgentLogin onLogin={setToken} />
      </>
    );
  }

  if (!me) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#F3F5F8] text-sm text-gray-500">
          {loadError || "Caricamento…"}
          {loadError && (
            <button type="button" onClick={() => setToken(null)} className={btnSoft}>
              Torna al login
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {seo}
      <div className="min-h-screen bg-[#F3F5F8] print:bg-white">
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 print:hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => navigate(LIST_PAGE)}
              className="flex items-center gap-3 min-w-0"
            >
              <Logo className="h-9 w-auto" />
              <span className="hidden sm:inline text-[10px] font-montserrat font-bold tracking-[0.2em] text-[#0FB36B]">
                PREVENTIVATORE
              </span>
            </button>
            <div className="flex items-center gap-3">
              <Avatar name={me.agent.name} />
              <span className="hidden sm:block text-sm text-[#0A1F44] leading-tight">
                {me.agent.name}
                <span className="block text-[11px] text-gray-400">{me.agent.role === "admin" ? "Amministratore" : "Standard"} · {me.agent.code}</span>
              </span>
              <button
                type="button"
                onClick={logout}
                aria-label="Esci"
                className="ml-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm text-gray-500 hover:text-[#0A1F44] hover:bg-gray-100"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Esci</span>
              </button>
            </div>
          </div>
        </header>

        {view === "new" ? (
          <OfferEditor
            me={me}
            token={token}
            onUnauthorized={onUnauthorized}
            onCancel={() => navigate(LIST_PAGE)}
            onSaved={(q) => {
              setQuotes((s) => [q, ...s]);
              navigate(offerPage(q), { replace: true });
            }}
          />
        ) : view === "edit" && openQuote && (openQuote.agent?.id === me.agent.id || me.agent.role === "admin") ? (
          <OfferEditor
            key={`edit-${openQuote.id}`}
            me={me}
            token={token}
            initial={openQuote}
            onUnauthorized={onUnauthorized}
            onCancel={() => navigate(offerPage(openQuote))}
            onSaved={(q) => {
              setQuotes((s) => s.map((x) => (x.id === q.id ? q : x)));
              navigate(offerPage(q), { replace: true });
            }}
          />
        ) : view === "detail" && openQuote ? (
          <OfferDetail
            key={openQuote.id}
            quote={openQuote}
            me={me}
            onBack={() => navigate(isArchived(openQuote) ? ARCHIVE_PAGE : LIST_PAGE)}
            onEdit={() => navigate(offerPage(openQuote, true))}
          />
        ) : (
          <OffersList
            me={me}
            quotes={quotes}
            loading={loadingQuotes || (Boolean(quoteId) && !quotesLoaded)}
            archive={view === "archive"}
            busy={busy}
            error={actionError}
            onCopy={copyQuote}
            onArchive={archiveQuote}
            onToggleArchive={() => {
              setActionError("");
              navigate(view === "archive" ? LIST_PAGE : ARCHIVE_PAGE);
            }}
            onNew={() => navigate({ p: "offers.new" })}
            onOpen={(q) => navigate(offerPage(q))}
          />
        )}
      </div>
    </>
  );
};

export default Preventivatore;
